import { lifecyclesOf, onlyWithSessions, useHandledKeys } from './message-lifecycle';
import { continuesGroup } from './message-markdown';
import { useMessageSessions } from '@src/hooks/conversation/useMessageAutomationSessions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { CheckCircle2, LifeBuoy, RefreshCw, RotateCcw } from 'lucide-react';
import {
  Agent,
  Conversation,
  DataSource,
  fetchConversations,
  FlowMessage,
  MessageThread,
  pickupConversation,
  settleTicket,
  QueryFilter,
  QueryRequest,
  TypeId,
  latestPointer,
  ChannelTransport,
  isSessionActive,
  isSessionTerminal,
  RemoteWorkerSession,
  Task,
  toplog,
  dataContext,
  dataManager,
} from '@sdk';
import { useEntityBatch } from '@src/components/entity-batch/EntityBatchHydrator';
import { SealedNoteLine, SealedValuesPanel } from './SealedValuesPanel';

const HUB_ONLY_KINDS = new Set(['sealed_value', 'sealed_ack', 'sealed_pending', 'sealed_request']);
import { claimTabSwitchReady, sinceTabSwitch } from '@src/navigation/tab-switch-state';
import { useAuth, useEntitiesQuery, useEntity, useOnTag, useProject } from '@sdk/react/hooks';
import type { FlowEvent } from '@sdk/tags/EventBus';
import type { ITask, TaskableMessage } from '@sdk/entities/task';
import { isClosedConversation, isHelpdeskKind } from '@sdk/entities/conversation';
import { replyPickedUp, ticketAffordances } from './ticket-affordances';
import { ThreadHeader } from './ThreadHeader';
import { ThreadStack } from './ThreadStack';
import { useAttentionPolling } from '@src/components/data-sources/useAttentionPolling';
import { syncConversationMessages } from '@src/components/stream-inbox-view/stream-inbox-api';
import { FlowMessageKind, markFlowMessagesReceived } from '@sdk/entities/flow-message';
import { FlowMessageBubble } from './FlowMessageBubble';
import { messageSummary } from './ChannelMessageExtras';
import { SessionEventLine } from './SessionEventLine';
import { SessionCard } from './SessionCard';
import { MessageComposer } from './MessageComposer';
import { ConversationActions, ConversationEndedLine } from './ConversationActions';
import { useImplementPlan } from './useImplementPlan';
import { useLocalUser } from './useLocalUser';
import { useMarkLatestRead } from './useMarkLatestRead';
import { useMembers } from '@src/hooks/use-members';
import {
  buildConversationItems,
  groupThreadItems,
  itemThreadId,
  partitionThreaded,
  flatThreadIdsOf,
  ConversationItemKind,
  anchorSessionItems,
  shouldShowSoloSendNotice,
  type ConversationItem,
} from './conversation-items';
import { indexCalls, runningProcessCalls } from './process-call';
import { resolveAttachmentProjectId } from './conversation-context-aggregation';
import { isHubOnly } from '@src/navigation/hub-runtime';
import { useConversationMessageAttachments } from './useMessageAttachments';
import { DockPointer } from '@src/navigation/DockPointer';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { useProcessesForTarget } from '@src/components/entity-execution-panel/hooks/useProcessesForTarget';
import { ConversationLiveActivity } from './ConversationLiveActivity';
import { mostRecentProcess } from '@src/utils/process-recency';
import { sessionRole, useConversationSessions } from '@src/hooks/useConversationSessions';
import { useMyEmail } from '@src/hooks/use-my-email';
import { taskIt, useMessageTasks } from './task-it';
import {
  conversationMessagesRequest,
  loadedPointers,
  olderConversationMessagesRequest,
  oldestCreatedDate,
} from './conversation-messages-query';
import { useApproveLiveSession } from '@src/components/collaboration/useApproveLiveSession';
import { failedPromptOf, pendingPromptOf } from './session-turns';
import { isSessionExpired } from './session-card-state';
import { LiveSessionActivity } from './LiveSessionActivity';
import { useRetryFailedPrompt } from './useRetryFailedPrompt';
import { useCloudLoginGate } from '@src/hooks/use-cloud-login-gate';
import { notify } from '@src/notifications';
import type { WorkerType } from './conversation-session-constants';
import { buildConversationStatusPrompt, buildMessageStartPrompt } from './prompt-building';
import { useConversationSession } from './useConversationSession';

interface ConversationViewProps {
  conversationId: string;
  /** Optional. Task-bound conversations (inbound .flowmsg) pass it; project-scoped do not. */
  task?: ITask | null;
  senderName?: string;
  /** Wraps any action that needs a `cwd`/project. Provided by the parent (SharedTaskView / TaskDetailPanel). */
  ensureMapped?: (continuation: () => void | Promise<void>) => void;
  /** Currently-selected message ids. Multiple entries when the selection
   *  came from clicking a context entity that's attached to more than one
   *  message — every one of those bubbles should light up together. */
  selectedMessageIds?: readonly string[];
  /** Called when the user clicks (or starts editing) a single message. The
   *  parent typically collapses the selection to just this bubble. */
  onSelectMessage?: (messageId: string) => void;
  /** Reports the most recent message id so the parent can default-select it. */
  onMostRecentMessageChange?: (messageId: string | null) => void;
  /** Open an executed message's run in the drawer's Runs tab, focused on it.
   *  Fired by the per-message run-status one-liner (not by executing). */
  onOpenRun?: (processId: string) => void;
  /** URL-carried thread filter: show only this thread's messages, unpacked.
   *  Null = every thread, each packed into one row. */
  threadId?: string | null;
  /** Open a thread (id) or return to the packed list (null). URL-first — the
   *  view never filters itself, it asks the host to navigate. */
  onThreadNavigate?: (threadId: string | null) => void;
  /** Restrict this view and every mutation to one Agent's formal stream inbox. */
  agentId?: string | null;
}

/** Geometry shared by the ticket-header actions. Only the colour differs
 *  between them, so a padding or type-scale tweak belongs in one place. */
const ticketActionClassName =
  'flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';

export function ConversationView({
  conversationId,
  task,
  ensureMapped,
  selectedMessageIds,
  onSelectMessage,
  onMostRecentMessageChange,
  onOpenRun,
  threadId,
  onThreadNavigate,
  agentId,
}: ConversationViewProps) {
  const { t } = useLingui();
  const conversationTypeId = useMemo(() => new TypeId(Conversation.type, conversationId), [conversationId]);
  const { data: conversation, refetch } = useEntity<Conversation>(conversationTypeId);
  const { localUser } = useLocalUser();

  // Resolve the conversation's headless run + its live status ONCE here, rather
  // than per bubble, and hand it to every executed message's run-status
  // one-liner. Load-bearing assumption: the backend reuses ONE run per
  // conversation (execute_prompt.py `_reuse_or_spawn_headless`), so the
  // most-recent run IS the run each executed message spawned. Same query key as
  // the Runs tab, so the two subscriptions dedup.
  const { processes: convRuns } = useProcessesForTarget(conversationTypeId.toString());
  const convRun = useMemo(() => mostRecentProcess(convRuns), [convRuns]);
  // The run entity carries live worker status now (headless turns broadcast
  // mid-turn), so read it directly instead of deriving over the stream.
  const convRunStatus = convRun?.workerStatus ?? null;

  // Every live session of this conversation, resolved ONCE and handed down:
  // the feed pins each to its opening message; each card reads its own row.
  const { byId: sessionsById, anchors: sessionAnchors } = useConversationSessions(conversationId);
  const { approve: approveSession, picker: approveSessionPicker } = useApproveLiveSession();
  const retryFailedPrompt = useRetryFailedPrompt();

  // Member roster used to resolve a message's hub-authoritative sender_id to
  // a display name. `useMembers` is the single precedence point: the live
  // entity-cache roster wins once populated (kept fresh by membership-change
  // fanout frames and list-refresh upserts), with the hook's one-shot hub
  // fetch as the initial-load source while the cache is cold.
  // `rosterReady` is true once the hub has answered for this conv at least
  // once (success or failure) — FlowMessageBubble uses it to gate the alert
  // glyph so legitimate load windows don't flash UNRESOLVED.
  const { members: memberRoster, ready: rosterReady, refresh: refreshMembers } = useMembers(conversationTypeId);
  const participants = memberRoster;

  const [agentScope, setAgentScope] = useState<Awaited<ReturnType<Agent['streamInboxScope']>> | null>(null);
  const refreshAgentScope = useCallback(async () => {
    if (!agentId) {
      setAgentScope(null);
      return;
    }
    try {
      setAgentScope(await new Agent({ id: agentId }).streamInboxScope());
    } catch {
      setAgentScope({ agent_id: agentId, source_id: null, conversation_ids: [], thread_ids: [], flow_message_ids: [] });
    }
  }, [agentId]);
  useEffect(() => void refreshAgentScope(), [conversation?.message_ids, refreshAgentScope]);
  const allowedMessageIds = useMemo(
    () => (agentId ? new Set(agentScope?.flow_message_ids ?? []) : null),
    [agentId, agentScope?.flow_message_ids],
  );
  const storedPointers = useMemo(
    () =>
      (conversation?.conversationMessageIds ?? []).filter(
        (pointer) => !allowedMessageIds || allowedMessageIds.has(pointer.id),
      ),
    // conversationMessageIds is a parsed view over the message_ids JSON field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversation?.message_ids, allowedMessageIds],
  );

  // Local-only drafts attached to this conversation. Filtered to the local
  // user so a counterparty's stray draft never renders here. Match is built
  // as an explicit $AND/$EQ tree — the SDK's multi-key plain-object shorthand
  // doesn't recursively wrap operands, which makes real-time validate() crash.
  const draftsRequest = useMemo(() => {
    const eqClauses: Array<{ op: string; operands: unknown[] }> = [
      { op: '$EQ', operands: ['conversation_id', conversationId] },
      { op: '$EQ', operands: ['is_draft', true] },
    ];
    if (localUser?.id) {
      eqClauses.push({ op: '$EQ', operands: ['sender_id', localUser.id] });
    }
    return new QueryRequest({
      type: FlowMessage.type,
      scope: [],
      name: `drafts:${conversationId}`,
      query: new QueryFilter({
        match: { op: '$AND', operands: eqClauses } as Record<string, unknown>,
      }),
    });
  }, [conversationId, localUser?.id]);
  const { data: draftMessages = [] } = useEntitiesQuery<FlowMessage>(draftsRequest, {
    // Drafts are a desktop feature (local rows); the hub page has none to show.
    enabled: !!conversationId && !isHubOnly(),
  });

  // All FlowMessages in this conversation, fetched in ONE live query (replaces
  // the per-bubble `useEntity` N+1). The query is the source of truth for
  // message bodies; the conversation's pointer list still drives order. Match
  // is wrapped in $AND for the same reason as the drafts query (the SDK's
  // plain-object shorthand doesn't recursively wrap operands). Capped at
  // CONVERSATION_MESSAGES_WINDOW newest rows so long-running conversations
  // don't fetch + watch O(total) entities on every open; older messages
  // load on demand via a `created_date $LT` cursor extension here.
  const messagesRequest = useMemo(() => conversationMessagesRequest(conversationId), [conversationId]);
  const {
    data: windowMessages = [],
    refetch: refetchConversationMessages,
    isSuccess: messagesLoaded,
    error: messagesError,
    hasMore: windowHasMore = false,
  } = useEntitiesQuery<FlowMessage>(messagesRequest, {
    enabled: !!conversationId,
  });
  // Older messages, a page at a time past the live window: `created_date` older than the oldest
  // loaded (a keyset). Kept per conversation; a new conversation starts from its window again.
  const [older, setOlder] = useState<{ conversationId: string; rows: FlowMessage[]; hasMore: boolean } | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const olderRows = older?.conversationId === conversationId ? older.rows : null;
  const hasOlder = olderRows ? !!older?.hasMore : windowHasMore;
  const conversationMessages = useMemo(() => {
    if (!olderRows?.length) return windowMessages;
    const held = new Set(windowMessages.map((m) => m.id));
    return [...windowMessages, ...olderRows.filter((m) => !held.has(m.id))];
  }, [windowMessages, olderRows]);
  const loadOlder = useCallback(async () => {
    const before = oldestCreatedDate(conversationMessages);
    if (!conversationId || !before || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const rows = await dataManager.query<FlowMessage>(olderConversationMessagesRequest(conversationId, before), true);
      const more = (rows as unknown as { has_more?: boolean }).has_more ?? false;
      setOlder((prev) => ({
        conversationId,
        rows: [...(prev?.conversationId === conversationId ? prev.rows : []), ...rows],
        hasMore: more,
      }));
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, conversationMessages, loadingOlder]);
  // The order of the messages: the conversation's pointer log (``message_ids``) where it keeps
  // one; a conversation without one (the hub keeps no such log) is ordered by the messages
  // themselves — otherwise it read "No messages yet".
  // A conversation WITH a log is ordered by it even when the agent scope leaves none of it (scope
  // pending, empty or failed): falling back to the loaded messages then drew — and acked — rows
  // outside the scope. The fallback is held to the same scope.
  const hasPointerLog = (conversation?.conversationMessageIds?.length ?? 0) > 0;
  const pointers = useMemo(
    () =>
      hasPointerLog
        ? storedPointers
        : conversationMessages
            .filter((m) => !allowedMessageIds || allowedMessageIds.has(m.id))
            .map((m) => ({ id: m.id, type: FlowMessage.type, ts: String(m.created_date ?? '') }))
            .sort((a, b) => {
              // An undated message sorts last; equal times keep a stable order by id.
              const ta = Date.parse(a.ts);
              const tb = Date.parse(b.ts);
              const ka = Number.isNaN(ta) ? Infinity : ta;
              const kb = Number.isNaN(tb) ? Infinity : tb;
              return ka === kb ? a.id.localeCompare(b.id) : ka - kb;
            }),
    [conversationMessages, storedPointers, hasPointerLog, allowedMessageIds],
  );
  // `tab_switch` ready point for a conversation tab: the first frame after its
  // message window arrived (or failed). Once per mounted conversation.
  const readyTracedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId || readyTracedRef.current === conversationId) return;
    if (messagesError) {
      readyTracedRef.current = conversationId;
      toplog.log(
        'tab_switch',
        `error ${sinceTabSwitch()} sink=conversation_messages conversation=${conversationId.slice(0, 8)} err:`,
        messagesError,
      );
      return;
    }
    if (!messagesLoaded) return;
    readyTracedRef.current = conversationId;
    if (!toplog.isOn('tab_switch')) return;
    const count = conversationMessages.length;
    requestAnimationFrame(() => {
      if (!claimTabSwitchReady()) return;
      toplog.log(
        'tab_switch',
        `ready ${sinceTabSwitch()} kind=conversation mode=cold conversation=${conversationId.slice(0, 8)} messages=${count}`,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- logs a count, not a dependency
  }, [conversationId, messagesLoaded, messagesError]);
  const conversationMessagesKey = conversationMessages
    .map((message) => message.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!agentId || !conversationMessagesKey) return;
    // Source projection writes the FlowMessage and the conversation pointer in
    // one backend pass, but their live-query frames are independent. A new raw
    // message is therefore the reliable prompt to pull both the pointer list
    // and the Agent's formal source scope before rendering it.
    void Promise.all([refetch(), refreshAgentScope()]).catch(() => {
      // Keep the already-rendered thread during a transient refresh failure.
    });
  }, [agentId, conversationMessagesKey, refetch, refreshAgentScope]);
  const messagesById = useMemo(() => {
    const entries: Array<[string, FlowMessage]> = [];
    for (const message of conversationMessages) {
      if (message.id && (!allowedMessageIds || allowedMessageIds.has(message.id))) entries.push([message.id, message]);
    }
    return new Map(entries);
  }, [allowedMessageIds, conversationMessages]);

  // Pull this conversation's hub messages once per open (and whenever the
  // pointer set changes — a new reply landed). The backend syncs only the
  // new/changed messages in a single request (LWW by updated_date); the live
  // `messagesById` query above renders whatever lands, so no per-message
  // backfill loop or remount-keying is needed.
  useEffect(() => {
    // The hub page reads the hub itself; there is no local copy to pull into.
    if (!conversationId || isHubOnly()) return;
    void syncConversationMessages(conversationId, agentId ?? undefined).catch(() => {
      // Hub offline / not configured — the local query still renders what we
      // already have; avoid surfacing transient sync failures to the user.
    });
    // Re-sync when the pointer set changes so brand-new replies pull through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, conversationId, pointers.map((p) => p.id).join(',')]);

  // Implement Plan lifecycle — spawn + watch + open. See `useImplementPlan.ts`
  // for the full lifecycle docs. The hook returns `runImplementPlan` to wire
  // into each bubble's chip and a single `openPlanSession` that flips every
  // spec-bearing bubble to the "Open Plan Implementation Session" link the
  // instant any session in the thread exists (live or in-flight).
  // A message's ⋮ menu starts a worker on THAT message — the header's launch
  // lifecycle, one instance for the whole feed, with a message-pinned prompt.
  const buildStatusPrompt = useCallback(() => buildConversationStatusPrompt(conversationTypeId), [conversationTypeId]);
  const { launch: launchWorker } = useConversationSession({
    conversation: conversation ?? null,
    ensureMapped,
    buildPrompt: buildStatusPrompt,
  });
  const launchWorkerOnMessage = useCallback(
    (messageId: string, worker: WorkerType) =>
      launchWorker(
        worker,
        () => buildMessageStartPrompt(conversationTypeId, new TypeId(FlowMessage.type, messageId)),
        messageId,
      ),
    [launchWorker, conversationTypeId],
  );

  const { runImplementPlan, openPlanSession } = useImplementPlan({
    task,
    conversationId,
    pointers,
    ensureMapped,
  });

  // View Plan opens the spec's `.md` in the Milkdown dock — read-write,
  // same target the Shared Context "View" spec row navigates to.
  // `MessageBubble` does the spec-TypeId lookup per bubble and invokes this
  // with the spec id.
  const { navigation: dockNavigation } = useDockNavigation();
  const runViewPlan = useCallback(
    (specId: string) => {
      dockNavigation.openDock(DockPointer.forSpec(specId));
    },
    [dockNavigation],
  );

  // Bubbles for the loaded messages only: an older pointer waits for "load older" instead of
  // fetching its message on its own.
  const shownPointers = useMemo(
    () => (messagesLoaded ? loadedPointers(pointers, new Set(messagesById.keys())) : pointers),
    [messagesLoaded, pointers, messagesById],
  );
  const orderedItems = useMemo(
    () => buildConversationItems(shownPointers, draftMessages),
    [shownPointers, draftMessages],
  );

  // What this conversation's channel is — the backend's `channel_spec`. A reply goes out
  // through a data source only when the channel's transport says so; Flowpad's own chat is
  // a channel too, not the absence of one. A hub runtime carries no spec: its rows are the
  // hub's own conversations, which never reply through a local source.
  const channelSpec = conversation?.channel_spec ?? null;
  const channel = channelSpec?.transport === ChannelTransport.Source ? channelSpec.name : undefined;
  // The message the composer answers — local composer state, not navigation (nothing is shown
  // differently; the next send quotes it).
  const [replyTo, setReplyTo] = useState<FlowMessage | null>(null);
  useEffect(() => setReplyTo(null), [conversationId]);
  // What the channel lets a person do to one message — read off its spec. Reply is the `replies`
  // trait (Flowpad's own chat included); a native message is answered by its hub id, a channel's
  // by its origin.
  const channelTraits = channelSpec
    ? {
        quotes: !!channelSpec.quotes,
        reacts: !!channelSpec.reacts,
        native: channelSpec.transport === ChannelTransport.Flowpad,
      }
    : null;
  // Each message come in on the channel: where it is on its way to an answer (live statuses + the feed's answers).
  // A channel nobody else answers (the person's own — their Tasks channel) has no such way: no line.
  const answered = !!channel && conversation?.channel_answered !== false;
  const handledKeys = useHandledKeys(answered ? conversation?.channel_source_id : null);
  // The sessions automations started on this conversation's messages (one live query): the chip on each
  // message, and the lifecycle line's handling / didn't-finish states.
  const { automation: automationSessions, manual: manualSessions } = useMessageSessions(conversationId);
  const lifecycles = useMemo(() => {
    // A channel nobody here answers still shows the way of a message an automation's agent took.
    if (!answered && automationSessions.size === 0) return null;
    const ordered = orderedItems.flatMap((it): FlowMessage[] => {
      const fm = it.kind === ConversationItemKind.POINTER ? messagesById.get(it.messageId) : undefined;
      return fm ? [fm] : [];
    });
    const all = lifecyclesOf(ordered, handledKeys, automationSessions);
    return answered ? all : onlyWithSessions(all, automationSessions);
  }, [answered, orderedItems, messagesById, handledKeys, automationSessions]);
  // "Automate messages like this": the builder, prefilled with this channel and this message. URL-first.
  const handleAutomate = useCallback(
    (fm: FlowMessage) =>
      dockNavigation.openDock(
        DockPointer.forAutomations({
          creating: 'message',
          source: conversation?.channel_source_id ?? null,
          message: fm.id ?? null,
        }),
      ),
    [dockNavigation, conversation?.channel_source_id],
  );
  const quotedFor = (fm: FlowMessage | null) => {
    if (!fm?.reply_to_id) return null;
    const parent = messagesById.get(fm.reply_to_id);
    if (!parent) {
      // Outside the loaded window (or not here yet): still say it is a reply, and open the
      // conversation on that message — a navigation, so the loader resolves it.
      const parentId = fm.reply_to_id;
      return {
        sender: t`Earlier message`,
        text: t`Open the message this replies to`,
        onJump: () =>
          dockNavigation.openDock(
            DockPointer.forConversation(conversationId, { messageId: parentId, agentId: agentId ?? null }),
          ),
      };
    }
    const jump = () =>
      document
        .querySelector(`[data-testid="message-bubble-${parent.id}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return { ...messageSummary(parent, t`Someone`), onJump: jump };
  };

  // Attention-driven polling: while this source-backed conversation is the
  // SELECTED dock, keep its DataSource due (request_poll on an interval) so
  // new messages land fast; deselect and the requests stop on their own.
  const attentionSourceId = agentId
    ? (agentScope?.source_id ?? undefined)
    : (conversation?.channel_source_id ?? undefined);
  useAttentionPolling(attentionSourceId, conversationId);

  // The ingest sync boundary is too early: stream inbox projection runs as a detached
  // subscriber and writes the FlowMessage + conversation pointer afterward.
  // Refresh on the existing post-projection event instead, scoped to this
  // Agent's DataSource so another active source cannot disturb this thread.
  useOnTag(
    'stream_inbox.*.message.projected',
    () => {
      if (!agentId || !attentionSourceId) return;
      void Promise.all([refetch(), refetchConversationMessages(), refreshAgentScope()]).catch(() => {
        // Keep the already-rendered thread during a transient refresh failure.
      });
    },
    {
      scope: [attentionSourceId ? `data_source:${attentionSourceId}` : 'data_source:__inactive__'],
    },
  );

  // What is in flight. Local state, because the line must appear the instant
  // the user hits Send — the worker's process does not exist yet. Cleared when
  // the reply lands, which is the only honest signal it is no longer sending.
  const [sendingText, setSendingText] = useState<string | null>(null);
  // A channel reply that did not go: the send runs after its request returned, so the channel's refusal arrives
  // as a live signal (`stream_inbox.<provider>.reply.failed`) and replaces the "Sending" line, with its words.
  const [notSent, setNotSent] = useState<{ text: string; reason: string } | null>(null);
  useOnTag(
    'stream_inbox.*.reply.failed',
    (event: FlowEvent) => {
      const data = (event.data ?? {}) as { conversation_id?: string; reason?: string };
      if (data.conversation_id !== conversationId) return;
      setNotSent({ text: sendingText ?? '', reason: String(data.reason ?? '') });
      setSendingText(null);
    },
    { target: `conversation:${conversationId}` },
  );

  useEffect(() => {
    if (sendingText) setSendingText(null);
    // Intentionally keyed on the pointer count alone: a new message in this
    // conversation IS the arrival, whichever direction it came from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointers.length]);

  // Authoritative per-thread sizes. The feed query is windowed, so counting
  // the loaded messages would undercount a long thread; MessageThread carries
  // the real number and this is the only reason the entity is fetched here.
  const threadsRequest = useMemo(
    () =>
      new QueryRequest({
        type: MessageThread.type,
        name: `threads:${conversationId}`,
        query: new QueryFilter({
          match: { op: '$EQ', operands: ['conversation_id', conversationId] },
        }),
      }),
    [conversationId],
  );
  // The packed view reads the counts; a thread-filtered view reads its own row for the header.
  const { data: threads = [] } = useEntitiesQuery<MessageThread>(threadsRequest, {
    enabled: !!conversationId,
  });
  const currentThread = threadId ? (threads.find((th) => th.id === threadId) ?? null) : null;
  const threadCounts = useMemo(
    () =>
      new Map(
        threads
          .filter((th) => !agentId || agentScope?.thread_ids.includes(th.id ?? ''))
          .map((th) => [th.id ?? '', th.message_count ?? 0]),
      ),
    [agentId, agentScope?.thread_ids, threads],
  );
  const flatThreadIds = useMemo(() => flatThreadIdsOf(threads), [threads]);

  // Two views of one feed, chosen by the URL:
  //   ?thread=<id> → only that thread's messages, unpacked
  //   (absent)     → every thread packed into one row, internal chat flat
  //
  // Threaded and session messages are disjoint — an ingested email never has a
  // `remote_worker_session_id` — so each grouper gets its own slice. Session
  // messages collapse onto the message that opened the session (the anchor);
  // follow-ups, replies and lifecycle lines are hidden from the thread and
  // live in the session view.
  // A prompt call's answer is a message of its own on the wire, but it reads as the call's status:
  // it renders on the call's card, never as a bubble.
  const calls = useMemo(() => indexCalls(messagesById.values()), [messagesById]);
  // Calls running on this machine, asked once per conversation — and only while a call is still
  // unanswered — so a card mounting mid-run says "working…".
  const [runningCalls, setRunningCalls] = useState<ReadonlySet<string>>(() => new Set());
  const askRunning = calls.unanswered;
  useEffect(() => {
    if (!askRunning) return;
    let live = true;
    void runningProcessCalls(conversationId)
      .then((ids) => live && setRunningCalls(new Set(ids)))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [conversationId, askRunning]);
  const promptCallFor = (id: string) => {
    const call = calls.callByMessage.get(id);
    if (!call) return null;
    return { call, answer: calls.answerByCall.get(call.call_id) ?? null, running: runningCalls.has(call.call_id) };
  };
  const groupedItems = useMemo(() => {
    const getFm = (id: string) => messagesById.get(id) ?? null;
    const feed = orderedItems.filter(
      (it) => !(it.kind === ConversationItemKind.POINTER && calls.answerMessageIds.has(it.messageId)),
    );
    if (threadId) {
      const inThread = feed.filter((it) => itemThreadId(it, getFm) === threadId);
      return anchorSessionItems(inThread, getFm, sessionAnchors);
    }
    const { threaded, plain } = partitionThreaded(feed, getFm, flatThreadIds);
    return [
      ...groupThreadItems(threaded, getFm, threadCounts),
      ...anchorSessionItems(plain, getFm, sessionAnchors),
    ].sort((a, b) => a.sortAt - b.sortAt);
  }, [orderedItems, messagesById, threadId, threadCounts, flatThreadIds, sessionAnchors, calls]);

  // A message right under one from the same sender, moments later, drops its avatar and name.
  const continuations = useMemo(() => {
    const keys = new Set<string>();
    let prev: { sender_id?: string | null; at: number } | null = null;
    for (const item of groupedItems) {
      const fm = item.kind === ConversationItemKind.POINTER ? messagesById.get(item.messageId) : null;
      if (item.kind !== ConversationItemKind.POINTER || !fm || fm.kind === FlowMessageKind.SESSION_EVENT || fm.kind === FlowMessageKind.SEALED_NOTE) {
        prev = null;
        continue;
      }
      const cur = { sender_id: fm.sender_id, at: Date.parse(item.timestamp) };
      if (continuesGroup(prev, cur)) keys.add(item.key);
      prev = cur;
    }
    return keys;
  }, [groupedItems, messagesById]);

  // Each live session's messages, in feed order: its card offers Retry on a failed prompt
  // (nothing answered it since) and names the prompt the host is working on.
  const messagesBySession = useMemo(() => {
    const bySession = new Map<string, FlowMessage[]>();
    for (const item of orderedItems) {
      const fm = item.kind === ConversationItemKind.POINTER ? messagesById.get(item.messageId) : item.draft;
      const sid = fm?.remote_worker_session_id;
      if (!fm || !sid) continue;
      const fms = bySession.get(sid);
      if (fms) fms.push(fm);
      else bySession.set(sid, [fm]);
    }
    return bySession;
  }, [orderedItems, messagesById]);
  const turnsBySession = useMemo(
    () =>
      new Map(
        [...messagesBySession].map(([sid, fms]) => [
          sid,
          { failed: failedPromptOf(fms), pending: pendingPromptOf(fms) },
        ]),
      ),
    [messagesBySession],
  );

  // Where the composer of an open thread writes: a native thread joins its root; a channel whose
  // replies only thread (email, Slack) answers the thread's newest message; a quoting channel's
  // thread is the chat itself, so a plain send already lands in it.
  const threadTarget = useMemo(() => {
    if (!threadId) return null;
    const title = currentThread?.title ?? '';
    if (!channel) return { title, rootId: currentThread?.thread_key || null };
    if (channelSpec?.quotes) return { title };
    const getFm = (id: string) => messagesById.get(id) ?? null;
    const newest = [...orderedItems]
      .reverse()
      .find((it) => it.kind === ConversationItemKind.POINTER && itemThreadId(it, getFm) === threadId);
    return { title, answerId: newest?.kind === ConversationItemKind.POINTER ? newest.messageId : null };
  }, [threadId, currentThread, channel, channelSpec?.quotes, orderedItems, messagesById]);

  // One row of the feed — a normal bubble or a draft bubble. A stray
  // kind=session_event row (a lifecycle line whose session the feed could not
  // anchor) renders as a slim centered system line.
  /** ``threadTask``: the task a collapsed thread is about — shown on the message the stack shows (its newest),
   *  so a task's thread offers its chips without expanding it. */
  const renderConversationItem = (item: ConversationItem, threadTask: Task | null = null, continuation = false) => {
    if (item.kind === ConversationItemKind.POINTER) {
      const id = item.messageId;
      const fm = messagesById.get(id) ?? null;
      // The pointer list lands before the message window: until the window
      // arrives every bubble would fetch itself and flash "Loading message…".
      if (!fm && !messagesLoaded && !messagesError) return null;
      if (fm?.kind === FlowMessageKind.SESSION_EVENT) {
        return <SessionEventLine key={item.key} text={fm.text ?? ''} />;
      }
      // Hub-only secured-transfer frames (a sealed value, its ack, a promise, a request) are never chat rows; one
      // that arrived without its kind is removed on the next sync — never shown meanwhile.
      if (fm && HUB_ONLY_KINDS.has(String(fm.kind))) return null;
      if (fm?.kind === FlowMessageKind.SEALED_NOTE) {
        return <SealedNoteLine key={item.key} text={fm.text ?? ''} />;
      }
      // One plan session per conversation. Once any session exists (or
      // is in-flight) every spec-bearing bubble shows the "Open" link
      // pointing at the same session — and the chip is suppressed
      // everywhere so we never offer to spawn a duplicate.
      return (
        <FlowMessageBubble
          key={item.key}
          messageId={id}
          fm={fm}
          timestamp={item.timestamp}
          task={task}
          participants={participants}
          rosterReady={rosterReady}
          run={convRun}
          runStatus={convRunStatus}
          onOpenRun={onOpenRun}
          onImplementPlan={task && !openPlanSession ? runImplementPlan : undefined}
          onOpenPlanSession={openPlanSession}
          onViewPlan={runViewPlan}
          isSelected={(selectedMessageIds ?? []).includes(id)}
          onSelect={onSelectMessage ? () => onSelectMessage(id) : undefined}
          isConversationOwner={isConversationOwner}
          onDeleteMessage={handleDeleteMessage}
          isHelpdesk={isHelpdeskConversation}
          viewerCloudUserId={cloudUserId}
          attachmentProjectId={attachmentProjectId}
          messageAttachments={attachmentsByMessage.get(id)}
          showEmailHeaders={!!agentId}
          channelTraits={channelTraits}
          lifecycle={lifecycles?.get(id) ?? null}
          onAutomate={conversation?.channel_source_id ? handleAutomate : undefined}
          automationSession={automationSessions.get(id) ?? null}
          session={manualSessions.get(id) ?? null}
          quoted={quotedFor(fm)}
          onReply={channelSpec?.replies ? setReplyTo : undefined}
          messageTask={taskOfMessage(id) ?? threadTask}
          onTaskIt={handleTaskIt}
          taskPeople={taskPeople}
          onLaunchWorker={launchWorkerOnMessage}
          promptCall={promptCallFor(id)}
          callPeerName={otherParticipant?.name ?? otherParticipant?.email ?? null}
          continuation={continuation}
        />
      );
    }
    const id = item.draft.id ?? '';
    return (
      <FlowMessageBubble
        key={item.key}
        messageId={id}
        fm={item.draft}
        timestamp={
          item.draft.created_date instanceof Date
            ? item.draft.created_date.toISOString()
            : (item.draft.created_date ?? '')
        }
        task={task}
        participants={participants}
        rosterReady={rosterReady}
        isDraft
        onDraftSent={() => void refetch()}
        isSelected={!!id && (selectedMessageIds ?? []).includes(id)}
        onSelect={onSelectMessage && id ? () => onSelectMessage(id) : undefined}
        attachmentProjectId={attachmentProjectId}
      />
    );
  };

  // "You're the only participant" notice — computed, never persisted. Shown
  // under the last message when the local user sends into a shared
  // conversation whose roster has shrunk to just them (everyone else left),
  // so they know nobody will see it. See shouldShowSoloSendNotice.
  const lastItem = orderedItems.length > 0 ? orderedItems[orderedItems.length - 1] : null;
  const lastMessageSenderId =
    lastItem?.kind === ConversationItemKind.POINTER ? (messagesById.get(lastItem.messageId)?.sender_id ?? null) : null;

  // Surface the most-recent message id so the parent's Context tab can default
  // to it when the user hasn't clicked anything yet.
  const mostRecentMessageId = useMemo<string | null>(() => {
    for (let i = orderedItems.length - 1; i >= 0; i--) {
      const item = orderedItems[i];
      const id = item.kind === ConversationItemKind.POINTER ? item.messageId : (item.draft.id ?? null);
      if (id) return id;
    }
    return null;
  }, [orderedItems]);
  useEffect(() => {
    onMostRecentMessageChange?.(mostRecentMessageId);
  }, [mostRecentMessageId, onMostRecentMessageChange]);

  // Pull the (earliest) selected message into view whenever the selection
  // changes. `block: nearest` makes this a no-op when the bubble is already
  // on screen, so single-bubble clicks don't yank the scroll. Cross-message
  // selections (entity click → N origins) scroll to the first one in
  // conversation order — same id every render, so we still only scroll on
  // genuine selection changes. The "set" identity is compared via the joined
  // string so a re-render of the same array doesn't re-fire.
  //
  // Two-phase (arm → fire) so URL deep links (/conversation/<id>/message/<id>)
  // work: on a cold load the selection lands before the bubbles have rendered
  // (messages stream in via the live query), so a single same-tick
  // querySelector would miss. The pending id stays armed until the target
  // exists in the DOM — the fire effect re-runs as messages/items land.
  const selectionKey = (selectedMessageIds ?? []).join(',');
  const scrollTargetId = (selectedMessageIds ?? [])[0] ?? null;
  const pendingScrollIdRef = useRef<string | null>(null);
  const prevSelectionKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevSelectionKeyRef.current;
    prevSelectionKeyRef.current = selectionKey;
    if (prev === selectionKey) return; // re-render of the same selection
    pendingScrollIdRef.current = scrollTargetId;
  }, [selectionKey, scrollTargetId]);
  useEffect(() => {
    const target = pendingScrollIdRef.current;
    if (!target) return;
    const el = document.querySelector<HTMLElement>(`[data-testid="message-bubble-${CSS.escape(target)}"]`);
    if (!el) return; // bubble not rendered yet — retry on the next data tick
    pendingScrollIdRef.current = null;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectionKey, orderedItems, messagesById]);

  // Read-ack emission: when the conversation panel is open, batch-mark all
  // current pointers as `received`. The hub honors monotonicity + sender-skip
  // server-side, so re-acking already-received or own-sent messages is a
  // cheap no-op. Debounced 250ms to coalesce bursts (e.g. catch-up).
  const ackedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // A channel's messages were delivered by the channel, not the hub: there is no hub receipt to send.
    if (channel || pointers.length === 0) return;
    const candidates = pointers.map((p) => p.id).filter((id) => id && !ackedRef.current.has(id));
    if (candidates.length === 0) return;
    const handle = setTimeout(() => {
      candidates.forEach((id) => ackedRef.current.add(id));
      void markFlowMessagesReceived(candidates).catch(() => {
        // Network/hub failure shouldn't crash the UI. Reset our tracker so
        // the next pointer change will retry.
        candidates.forEach((id) => ackedRef.current.delete(id));
      });
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, pointers.map((p) => p.id).join(',')]);

  // Help-desk (support) ticket: replies are masked to a single brand
  // identity, and the real responder's sender_id is intentionally absent from
  // the guest's (redacted) roster — so the bubble must not flag it as an
  // unknown sender. See HelpdeskConfig / the hub sender_name masking.
  const isHelpdeskConversation = isHelpdeskKind(conversation?.kind);
  const { project: currentProject } = useProject();
  const attachmentProjectId = resolveAttachmentProjectId(task, conversation, currentProject?.id);
  // "Task it": the tasks made from this conversation's messages (one query), and the one-click create.
  // Task it is a desktop feature: the hub page links no tasks to a conversation.
  const messageTasks = useMessageTasks(isHubOnly() ? null : conversationId);
  // A task thread's message names its task (the channel puts it in the message's shared context), so the
  // thread shows — and acts on — the same chips as the message the task was made from. One pass: which
  // task each message names, and so which task each thread is about.
  const refs = useMemo(() => {
    const byMessage = new Map<string, string>();
    const byThread = new Map<string, string>();
    for (const [id, fm] of messagesById) {
      const ref = (fm.sharedContextEntities ?? []).find((tid) => tid.type === Task.type);
      if (!ref) continue;
      byMessage.set(id, String(ref.id));
      if (fm.thread_id && !byThread.has(fm.thread_id)) byThread.set(fm.thread_id, String(ref.id));
    }
    return { byMessage, byThread, ids: [...new Set(byMessage.values())] };
  }, [messagesById]);
  const refTasks = useEntityBatch<Task>(Task.type, refs.ids);
  const refTaskById = useMemo(() => new Map(refTasks.map((task) => [task.id, task])), [refTasks]);
  const threadTaskOf = (threadId: string): Task | null => refTaskById.get(refs.byThread.get(threadId) ?? '') ?? null;
  const taskOfMessage = (id: string): Task | null =>
    messageTasks.get(id) ?? refTaskById.get(refs.byMessage.get(id) ?? '') ?? null;
  const myEmail = useMyEmail();
  const handleTaskIt = useCallback(
    (message: TaskableMessage) => void taskIt(message, { me: myEmail, projectId: attachmentProjectId }),
    [myEmail, attachmentProjectId],
  );
  // Staged bundle attachments (one query for the whole panel). Drives the
  // dashed staged chips + review modal in each bubble.
  const { byMessage: attachmentsByMessage } = useConversationMessageAttachments(conversationId);

  // The conversation owner (initiated_by == the local cloud user) may delete ANY
  // message; everyone may delete their own. The per-bubble sender check is done
  // inside FlowMessageBubble — this only supplies the owner half of the gate.
  const { cloudUser } = useAuth();
  const cloudUserId = cloudUser?.id ?? null;
  const taskPeople = useMemo(
    () => ({ members: participants ?? [], me: myEmail, cloudUserId }),
    [participants, myEmail, cloudUserId],
  );
  // Owner = the hub's initiated_by OR the local user holds role "owner" in the roster.
  const isConversationOwner =
    !!cloudUserId &&
    ((!!conversation?.initiated_by && conversation.initiated_by === cloudUserId) ||
      (participants ?? []).some((p) => p.user_id === cloudUserId && p.roles?.includes('owner')));

  // Open-to-read (URL-first): viewing a conversation marks its latest received message read —
  // the mutation lives on the mounted view, so the Stream Inbox row click / banner click / direct
  // link only navigate (single writer: navigation → view → action).
  const latestId = latestPointer(pointers)?.id;
  useMarkLatestRead(latestId ? messagesById.get(latestId) : undefined, cloudUserId, localUser?.id, agentId);

  // Delete a message everywhere. The loader/live-query owns the list, so we
  // only fire the SDK action and let the resulting data-op re-render the view
  // (no optimistic list mutation — URL/loader-first).
  const handleDeleteMessage = useCallback((messageId: string) => {
    void new FlowMessage({ id: messageId }).remove().catch((err) => {
      console.error('[conversation] delete message failed', messageId, err);
    });
  }, []);

  const [hubSyncing, setHubSyncing] = useState(false);
  // Full reload: messages AND members. The button is the user's explicit
  // "pull everything fresh from the hub" — so it force-reloads the roster
  // (refreshMembers re-fetches, bypassing the per-instance members cache),
  // re-syncs this conversation's messages, and re-reads the conversation
  // entity. Each leg is independent + best-effort so one offline hop doesn't
  // block the others.
  const handleRefresh = useCallback(async () => {
    setHubSyncing(true);
    try {
      await Promise.allSettled([
        // Pulling the hub into the local copy is the desktop's; the hub page reads the hub itself.
        ...(isHubOnly()
          ? []
          : [fetchConversations(agentId ?? undefined), syncConversationMessages(conversationId, agentId ?? undefined)]),
        refreshMembers(),
      ]);
      await refetch();
    } finally {
      setHubSyncing(false);
    }
  }, [agentId, refetch, refreshMembers, conversationId]);

  const [pickingUp, setPickingUp] = useState(false);
  const isParticipant = !!cloudUserId && (participants ?? []).some((p) => p.user_id === cloudUserId);
  const { canPickup, canSettle } = ticketAffordances({
    helpdesk: isHelpdeskConversation,
    cloudUserId,
    isOwner: isConversationOwner,
    isParticipant,
  });
  useEffect(() => {
    if (replyPickedUp(canPickup, lastMessageSenderId, cloudUserId)) void refreshMembers();
  }, [canPickup, lastMessageSenderId, cloudUserId, refreshMembers]);

  const showSoloNotice = shouldShowSoloSendNotice({
    remote: conversation?.remote === true,
    helpdesk: isHelpdeskConversation,
    rosterReady,
    participants: participants ?? [],
    cloudUserId,
    lastItem,
    lastMessageSenderId,
  });
  // The other participant of a 1:1 shared conversation is the HOST every
  // prompt runs on. The peer's user_id must be RESOLVED (an unresolved roster
  // row passes the filter with user_id null) before the composer offers to
  // run there.
  const otherParticipant = useMemo(
    () => (participants ?? []).find((p) => p.user_id !== cloudUserId) ?? null,
    [participants, cloudUserId],
  );
  const sessionHost = useMemo(
    () =>
      conversation?.remote === true &&
      rosterReady &&
      (participants ?? []).length === 2 &&
      !!cloudUserId &&
      !!otherParticipant?.user_id
        ? {
            name: otherParticipant.name ?? otherParticipant.email ?? null,
            hasOpenSession: [...sessionsById.values()].some((s) => !isSessionTerminal(s.status)),
          }
        : null,
    [conversation?.remote, rosterReady, participants, cloudUserId, otherParticipant, sessionsById],
  );
  const openLiveSession = useCallback(
    (sessionId: string) => {
      dockNavigation.openDock(DockPointer.forLiveSession(sessionId));
    },
    [dockNavigation],
  );
  // The composer's live-session icon: open the conversation's session — the backend
  // starts one (and asks the host) when none is open — and land in its view.
  const ensureCloudLogin = useCloudLoginGate();
  const startLiveSession = useCallback(async () => {
    if (!conversationId) return;
    const gate = await ensureCloudLogin();
    if (!gate.ok) {
      notify.error({ title: gate.error, forceToast: true });
      return;
    }
    try {
      const session = await RemoteWorkerSession.start(conversationId);
      openLiveSession(session.id);
    } catch (e) {
      notify.error({ title: t`Could not start the live session`, message: String(e), forceToast: true });
    }
  }, [conversationId, ensureCloudLogin, openLiveSession, t]);

  const [settling, setSettling] = useState(false);
  const isClosed = isClosedConversation(conversation?.status);

  const handleSettle = useCallback(async () => {
    setSettling(true);
    try {
      await settleTicket(conversationId, isClosed ? 'reopen' : 'close');
      await handleRefresh();
    } catch (err) {
      console.error('[conversation] settle failed', conversationId, err);
    } finally {
      setSettling(false);
    }
  }, [conversationId, isClosed, handleRefresh]);

  const handlePickup = useCallback(async () => {
    setPickingUp(true);
    try {
      await pickupConversation(conversationId);
      await handleRefresh();
    } catch (err) {
      console.error('[conversation] pickup failed', conversationId, err);
    } finally {
      setPickingUp(false);
    }
  }, [conversationId, handleRefresh]);

  return (
    <div className="space-y-3">
      {approveSessionPicker}
      {/* Its own row: a refusal spans the width under the buttons instead of squeezing beside them. */}
      {conversation && (
        <ConversationActions
          conversation={conversation}
          messages={conversationMessages}
          selfIds={[cloudUserId, localUser?.id, dataContext.bootstrapInfo?.user?.id]}
          onDone={() => void handleRefresh()}
        />
      )}
      <div className="flex items-center justify-end gap-1">
        {canPickup && (
          <button
            type="button"
            onClick={() => void handlePickup()}
            disabled={pickingUp}
            title={t`Join this support ticket. Replying picks it up too.`}
            data-testid="pickup-conversation-button"
            className={`${ticketActionClassName} border-violet-500/40 bg-violet-500/15 text-violet-600 hover:bg-violet-500/25 dark:text-violet-400`}
          >
            <LifeBuoy className="h-3 w-3" />
            {pickingUp ? <Trans>Picking up…</Trans> : <Trans>Pick up</Trans>}
          </button>
        )}
        {canSettle && (
          <button
            type="button"
            onClick={() => void handleSettle()}
            disabled={settling}
            title={isClosed ? t`Reopen this ticket` : t`Mark this ticket answered and take it off the desk's queue`}
            data-testid="settle-conversation-button"
            className={`${ticketActionClassName} border-border text-muted-foreground hover:bg-muted hover:text-foreground`}
          >
            {isClosed ? <RotateCcw className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
            {settling ? <Trans>Saving…</Trans> : isClosed ? <Trans>Reopen</Trans> : <Trans>Close ticket</Trans>}
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleRefresh()}
          title={channel ? t`Refresh (checks the channel for new messages)` : t`Refresh (pulls from hub)`}
          data-testid="refresh-conversation-button"
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${hubSyncing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {threadId && (
        <ThreadHeader
          title={currentThread?.title ?? ''}
          messageCount={currentThread?.message_count ?? null}
          onShowAll={onThreadNavigate ? () => onThreadNavigate(null) : undefined}
        />
      )}
      {hasOlder && orderedItems.length > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            disabled={loadingOlder}
            onClick={() => void loadOlder()}
            data-testid="conversation-load-older"
            className="rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
          >
            {loadingOlder ? <Trans>Loading…</Trans> : <Trans>Load older messages</Trans>}
          </button>
        </div>
      )}
      {orderedItems.length === 0 ? (
        <p className="text-xs italic text-muted-foreground/60">
          <Trans>No messages yet.</Trans>
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {groupedItems.map((item) => {
            if (item.kind === ConversationItemKind.THREAD_GROUP) {
              return (
                <ThreadStack
                  key={item.key}
                  messageCount={item.messageCount}
                  onOpenThread={onThreadNavigate ? () => onThreadNavigate(item.threadId) : undefined}
                >
                  {renderConversationItem(item.head, threadTaskOf(item.threadId))}
                </ThreadStack>
              );
            }
            if (item.kind === ConversationItemKind.SESSION_ANCHOR) {
              const session = sessionsById.get(item.sessionId) ?? null;
              const role = sessionRole(session, cloudUserId);
              const turns = turnsBySession.get(item.sessionId);
              const failedPrompt = turns?.failed;
              return (
                // The session's ONE line — its opening prompt (or request) is not
                // drawn as a bubble; the turns live in the session view.
                <div key={item.key} data-testid="session-anchor">
                  <SessionCard
                    sessionId={item.sessionId}
                    session={session}
                    role={role}
                    promptCount={item.promptCount}
                    replyCount={item.replyCount}
                    onOpen={() => openLiveSession(item.sessionId)}
                    onApprove={role === 'host' && session ? () => approveSession(session) : undefined}
                    onDecline={role === 'host' && session ? () => session.decline() : undefined}
                    onDisconnect={role !== 'observer' && session ? () => session.disconnect() : undefined}
                    lastPromptFailed={!!turns?.failed}
                    runningPrompt={turns?.pending ?? null}
                    chatProcessId={role === 'host' ? (session?.host_process_id ?? null) : null}
                    onRetry={
                      role === 'guest' && failedPrompt && conversationId
                        ? () => retryFailedPrompt(conversationId, item.sessionId, failedPrompt.text)
                        : undefined
                    }
                  />
                  {/* Only a live session: an old one's process may be busy with something else. */}
                  {role === 'host' &&
                    session &&
                    isSessionActive(session.status) &&
                    !isSessionExpired(session, Date.now()) && (
                      <div className="px-2">
                        <LiveSessionActivity session={session} />
                      </div>
                    )}
                </div>
              );
            }
            return renderConversationItem(item, null, continuations.has(item.key));
          })}
        </div>
      )}

      {/* A channel's conversation is answered by the owning agent's run, not by this pane —
          so show that run working, and a caller mid-sentence, while it happens. */}
      {channel && (
        <ConversationLiveActivity conversationId={conversationId} run={convRun} messageCount={pointers.length} />
      )}

      {showSoloNotice && (
        <p data-testid="solo-participant-notice" className="text-[11px] italic text-muted-foreground/70">
          <Trans>You're the only participant in this conversation — no one else will see this message.</Trans>
        </p>
      )}

      {/* Always render the reply composer. A pending draft (e.g. an "Approve &
          Execute" auto-reply awaiting review) renders above as its own editable
          bubble with Send/Discard — but it must not block composing a fresh reply.
          The plain composer never auto-creates a draft, so there's no duplication
          loop. */}
      {/* Deliberately does NOT name the outcome. Whether a reply is delivered
          or parked as a draft is the transport's business — Agentmail sends,
          the Gmail connector can only draft — and the composer learns which
          only when the send resolves. Copy that promised "drafted" was simply
          wrong half the time. The reply itself arrives in the feed by the
          ordinary ingest route once it exists. */}
      {sendingText && <SessionEventLine text={t`Sending in ${channelSpec?.title}: “${sendingText}”`} />}
      {notSent && (
        // Tinted with a red border; the words stay the foreground colour (red text on a dark theme does not read).
        <p
          className="mx-auto my-1 w-fit max-w-[80%] rounded border border-red-500/60 bg-red-500/10 px-2 py-1 text-xs"
          role="alert"
          data-testid="channel-reply-not-sent"
        >
          <span className="font-medium">
            <Trans>Not sent</Trans>
          </span>
          {notSent.text ? ` “${notSent.text}”` : ''} — {notSent.reason}
        </p>
      )}
      <ConversationEndedLine endedAt={conversation?.ended_at} />
      <SealedValuesPanel
        conversationId={conversationId}
        version={conversation?.updated_date ? String(conversation.updated_date) : null}
        members={memberRoster}
      />
      {/* Held until the message window lands: on an empty feed it would sit at the top for a frame, then jump. */}
      {(messagesLoaded || !!messagesError) && (
        <MessageComposer
          conversationId={conversationId}
          onSent={() => void refetch()}
          // A source-backed conversation replies into its channel, not the hub.
          channel={channel}
          onChannelSent={(text) => {
            setNotSent(null);
            setSendingText(text);
          }}
          placeholder={channelSpec && !channelSpec.home ? t`Reply in ${channelSpec.title}` : undefined}
          agentId={agentId ?? undefined}
          sessionHost={channelSpec && !channelSpec.hosts_sessions ? null : sessionHost}
          onStartLiveSession={() => void startLiveSession()}
          channelAcceptsFiles={!!channelSpec?.accepts_attachments}
          replyTo={
            replyTo
              ? { id: replyTo.id ?? '', ...messageSummary(replyTo, t`Someone`), inThread: !channelSpec?.quotes }
              : null
          }
          onClearReply={() => setReplyTo(null)}
          threadTarget={threadTarget}
          onTaskIt={handleTaskIt}
        />
      )}
    </div>
  );
}
